import React from 'react';
import { useField } from 'formik';
import {
  Box,
  FormHelperText,
  Grid,
  GridProps,
  IconButton,
  InputLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import Dropzone, { Accept, FileRejection } from 'react-dropzone';
import { SparkButton } from 'sharedComponents/UI';
import { useWorkspaceContext } from 'hooks/common';
import { useBlobStorage } from 'hooks/useBlobStorage';
import CloseIcon from 'components/SVG/CloseIcon';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSchemaContext } from './SparkForm';

const acceptedFileTypes: Accept = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    '.docx',
  ],
  'text/plain': ['.txt'],
};
const MAX_FILE_SIZE = 15000000;
type SparkFormFileUploaderProps = {
  label: string;
  helpText?: string;
  subHeaderText?: string;
  size?: GridProps['xs'];
  gridProps?: GridProps;
  containerName?: string;
  name?: string;
  onFileUpload?: (url: string) => void;
};
/**
 * JSX Component for uploading Single Files
 *

 */
function SparkFormFileUploader({
  size,
  name = 'test',
  gridProps,
  containerName = 'temp',
  onFileUpload,
  label,
  helpText,
  subHeaderText,
}: SparkFormFileUploaderProps) {
  const [field, meta, helper] = useField<string>(name);
  const { validationSchema } = useSchemaContext();
  const { getWorkspace } = useWorkspaceContext();
  const workSpace = getWorkspace();
  const isFieldRequired = React.useMemo(() => {
    try {
      const fieldSchema = validationSchema.describe().fields[name];
      // @ts-ignore
      const isRequiredSchema = fieldSchema.tests.find(
        //@ts-ignore
        (test) => test.name === 'requiredTest'
      );
      if (isRequiredSchema) {
        return true;
      }

      if ('optional' in fieldSchema) {
        return !fieldSchema.optional;
      }
      return false;
    } catch {
      return false;
    }
  }, [name, validationSchema]);

  const { handleFileUpload, handleDelete, uploads } = useBlobStorage(
    workSpace?.attributes.containerName || containerName
  );

  const handleSuccessfulUpload = async (
    acceptedFiles: File[],
    fileRejections: FileRejection[]
  ) => {
    const result = await handleFileUpload(acceptedFiles, fileRejections);
    if (result && result.length > 0) {
      const value = result[0] || '';
      helper.setValue(value.url || '');
      onFileUpload && onFileUpload(result[0].url || '');
    }
  };
  const handleDeleteClick: React.MouseEventHandler<HTMLButtonElement> = (
    event
  ) => {
    event.preventDefault();
    event.stopPropagation();
    handleDelete(uploads[0].blockBlobClient?.['_name'] || uploads[0].fileName);
    helper.setValue('');
  };
  const currentUpload = uploads.length > 0 ? uploads[0] : null;
  const isError = meta.touched && !!meta.error;
  return (
    <Grid item xs={size} container direction='column' {...gridProps}>
      <InputLabel
        sx={{ marginLeft: 1, display: 'flex', gap: 0.5, fontSize: 16 }}
      >
        <span>{isFieldRequired && '*'}</span>
        {label}
        {helpText && (
          <Tooltip title={helpText} placement='top' arrow enterTouchDelay={0}>
            <InfoOutlinedIcon fontSize='small' />
          </Tooltip>
        )}
      </InputLabel>
      {subHeaderText && (
        <Typography
          color='#A3A3A3'
          marginLeft={1.6}
          fontSize={12}
          textAlign='left'
        >
          {subHeaderText}
        </Typography>
      )}
      <Dropzone
        multiple={false}
        accept={acceptedFileTypes}
        maxSize={MAX_FILE_SIZE}
        onDrop={handleSuccessfulUpload}
      >
        {({ getRootProps, getInputProps }) => (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              border: '2px dashed #E8E8E8',
              borderRadius: '4px',
              padding: 2,
            }}
            {...getRootProps()}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexDirection: 'row',
                gap: '15px',
              }}
            >
              {!!field.value ? (
                currentUpload?.file.name || field.value
              ) : (
                <>
                  <SparkButton variant='contained' onClick={() => {}}>
                    Browse...
                  </SparkButton>
                  <Typography variant='body2'>
                    Drop your .txt, .pdf, .doc or .docx file here
                  </Typography>
                </>
              )}
              <input {...getInputProps()} />
            </Box>
            {currentUpload && !!field.value && (
              <IconButton
                aria-label='close'
                onClick={handleDeleteClick}
                sx={{
                  color: (theme) => theme.palette.primary.main,
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        )}
      </Dropzone>{' '}
      <FormHelperText
        sx={{
          color: (theme) => theme.palette.error.main,
          marginLeft: 1,
          marginBottom: 0.5,
        }}
      >
        {isError ? meta.error : ' '}
      </FormHelperText>
    </Grid>
  );
}

export default SparkFormFileUploader;
